import { Box, useTheme } from "@mui/material";

/**
 * Visual for the rating ask: the house chip idiom (PrivacyLockChip's
 * green-tint square) holding a star that lands once with a small settle and a
 * single ring pulse. Deliberately ONE star, not five: a row of stars reads as
 * rating UI (custom review prompts are disallowed) and implies the score
 * we're not allowed to ask for. Nothing loops — stillness reads as sincerity.
 */

interface RatingStarChipProps {
  size?: number;
}

export const RatingStarChip = ({ size = 56 }: RatingStarChipProps) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  return (
    <Box
      aria-hidden
      sx={{
        position: "relative",
        width: size,
        height: size,
        "@keyframes starLand": {
          "0%": { transform: "scale(0.5)", opacity: 0 },
          "70%": { transform: "scale(1.12)", opacity: 1 },
          "100%": { transform: "scale(1)", opacity: 1 },
        },
        "@keyframes starPulse": {
          "0%": { transform: "scale(1)", opacity: 0.5 },
          "100%": { transform: "scale(1.5)", opacity: 0 },
        },
        "& .star-mark": {
          transformOrigin: "center",
          animation: "starLand 0.35s ease-out 0.3s backwards",
        },
        "& .star-pulse": { animation: "starPulse 0.9s ease-out 0.5s backwards", opacity: 0 },
        "@media (prefers-reduced-motion: reduce)": {
          "& .star-mark, & .star-pulse": { animation: "none" },
        },
      }}
    >
      <Box
        className="star-pulse"
        sx={{
          position: "absolute",
          inset: 0,
          borderRadius: `${Math.round(size * 0.28)}px`,
          border: "2px solid rgba(34, 197, 94, 0.4)",
        }}
      />
      <Box
        sx={{
          width: size,
          height: size,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          borderRadius: `${Math.round(size * 0.28)}px`,
          backgroundColor: isDark ? "rgba(34, 197, 94, 0.15)" : "rgba(34, 197, 94, 0.1)",
          border: isDark
            ? "1px solid rgba(34, 197, 94, 0.3)"
            : "1px solid rgba(34, 197, 94, 0.2)",
        }}
      >
        <svg width={size * 0.5} height={size * 0.5} viewBox="0 0 24 24" fill="none">
          <path
            className="star-mark"
            d="M12 3.2l2.66 5.53 6.09.82-4.46 4.22 1.12 6.03L12 16.88l-5.41 2.92 1.12-6.03-4.46-4.22 6.09-.82z"
            stroke="#22c55e"
            strokeWidth="2"
            strokeLinejoin="round"
            fill="rgba(34, 197, 94, 0.35)"
          />
        </svg>
      </Box>
    </Box>
  );
};
